import React, { useEffect, useState, useRef } from 'react';
import { Check, Plus, WandSparkles, Download, MoreHorizontal } from 'lucide-react';
import { GenerateButton, SubmitButton, SecondaryButton } from '@/components/ui/Button';
import useTenantState from '@/states/tenant.state';
import { env } from '@/utils/constants/env';
import { axiosInstance } from '@/utils/lib/axios';

interface CompanyHeaderProps {
  title: React.ReactNode;
  description?: string;
  onGenerate?: () => void;
  onSave?: () => void;
  onPublish?: (published: boolean) => void;
  onCreate?: () => void;
  onExport?: () => void;
  onExportWord?: () => void;
  onExportPDF?: () => void;
  exportEndpoint?: string; // e.g., '/api/v1/compro/org-chart'
  generateLabel?: string;
  saveLabel?: string;
  createLabel?: string;
  extraAction?: React.ReactNode;
  isSaving?: boolean;
  isPublished?: boolean;
  showExport?: boolean;
  showGenerate?: boolean;
}

export default function CompanyHeader({
  title,
  description,
  onGenerate,
  onSave,
  onCreate,
  onExportWord,
  onExportPDF,
  exportEndpoint,
  generateLabel,
  saveLabel,
  createLabel,
  extraAction,
  isSaving,
  showExport = false,
  showGenerate = true,
}: Readonly<CompanyHeaderProps>) {
  const { currentTenant } = useTenantState();
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const handleExportWord = async () => {
    if (onExportWord) {
      onExportWord();
    } else if (exportEndpoint && currentTenant) {
      try {
        const url = `${exportEndpoint}/tenant/${currentTenant.id}/tree/download?format=word`;

        const response = await axiosInstance.get(url, {
          baseURL: env.apiTasklistBaseUrl,
          responseType: 'blob',
        });

        // Create blob link to download
        const blob = new Blob([response.data]);
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `organization-structure-${currentTenant.slug}.docx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error downloading Word:', error);
      }
    }
    setShowExportMenu(false);
  };

  const handleExportPDF = async () => {
    if (onExportPDF) {
      onExportPDF();
    } else if (exportEndpoint && currentTenant) {
      try {
        const url = `${exportEndpoint}/tenant/${currentTenant.id}/tree/download?format=pdf`;

        const response = await axiosInstance.get(url, {
          baseURL: env.apiTasklistBaseUrl,
          responseType: 'blob',
        });

        // Create blob link to download
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `organization-structure-${currentTenant.slug}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error downloading PDF:', error);
      }
    }
    setShowExportMenu(false);
  };

  return (
    <div className="sticky top-0 z-20 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 p-5 flex flex-col md:flex-row md:items-center justify-between gap-2 md:flex-wrap xl:flex-nowrap shadow-sm">
      <div className="flex flex-col flex-1 basis-64 min-w-0">
        <h1 className="font-semibold text-lg truncate">{title}</h1>
        {description && <p className="text-sm text-slate-500 dark:text-slate-300 mt-1">{description}</p>}
      </div>

      <div className="flex flex-wrap items-center gap-3 min-w-0">
        {extraAction && <div className="mr-1">{extraAction}</div>}

        {showGenerate !== false &&
          onGenerate &&
          (generateLabel === 'Batal' ? (
            <SecondaryButton onClick={onGenerate} className="flex items-center gap-1">
              {generateLabel}
            </SecondaryButton>
          ) : (
            <GenerateButton onClick={onGenerate} className="flex items-center gap-1">
              <WandSparkles size={16} />
              {generateLabel || 'Hasilkan'}
            </GenerateButton>
          ))}

        {showExport && (
          <div className="relative" ref={exportMenuRef}>
            <SubmitButton onClick={() => setShowExportMenu(!showExportMenu)} className="flex items-center gap-1">
              <Download size={16} />
              Ekspor
              <MoreHorizontal size={16} className="ml-1" />
            </SubmitButton>

            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={handleExportWord}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <Download size={16} />
                  Unduh Word
                </button>
                <button
                  onClick={handleExportPDF}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <Download size={16} />
                  Unduh PDF
                </button>
              </div>
            )}
          </div>
        )}

        {onSave && (
          <SubmitButton onClick={onSave} disabled={isSaving} className="flex items-center gap-1">
            <Check size={16} />
            {isSaving ? 'Menyimpan...' : saveLabel || 'Simpan'}
          </SubmitButton>
        )}

        {onCreate && (
          <SubmitButton onClick={onCreate} className="flex items-center gap-1">
            <Plus size={16} />
            {createLabel || 'Tambah'}
          </SubmitButton>
        )}
      </div>
    </div>
  );
}
